import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { useCompany } from '@/contexts/CompanyContext'

interface NavigationBadges {
  messages: { count: number; variant: 'urgent' | 'pending' | null }
  quotes: { count: number; variant: 'urgent' | 'pending' | null }
  jobs: { count: number; variant: 'urgent' | 'pending' | null }
  invoices: { count: number; variant: 'urgent' | 'pending' | null }
}

export const useNavigationBadges = () => {
  const { currentCompany } = useCompany()

  return useQuery({
    queryKey: ['navigation-badges', currentCompany?.id],
    queryFn: async (): Promise<NavigationBadges> => {
      if (!currentCompany) {
        return {
          messages: { count: 0, variant: null },
          quotes: { count: 0, variant: null },
          jobs: { count: 0, variant: null },
          invoices: { count: 0, variant: null }
        }
      }

      const badges: NavigationBadges = {
        messages: { count: 0, variant: null },
        quotes: { count: 0, variant: null },
        jobs: { count: 0, variant: null },
        invoices: { count: 0, variant: null }
      }

      // Get failed message deliveries (urgent - red)
      const { count: failedMessages } = await supabase
        .from('message_errors')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', currentCompany.id)
        .eq('resolved', false)

      if (failedMessages && failedMessages > 0) {
        badges.messages = { count: failedMessages, variant: 'urgent' }
      }

      // Get new/pending quote requests (pending - blue)
      const { count: pendingQuotes } = await supabase
        .from('quotes')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', currentCompany.id)
        .eq('status', 'draft')

      if (pendingQuotes && pendingQuotes > 0) {
        badges.quotes = { count: pendingQuotes, variant: 'pending' }
      }

      // Get jobs needing attention
      // First check for overdue jobs (urgent - red)
      const today = new Date().toISOString().split('T')[0]
      const { count: overdueJobs } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', currentCompany.id)
        .lt('scheduled_date', today)
        .in('status', ['scheduled', 'in_progress'])

      if (overdueJobs && overdueJobs > 0) {
        badges.jobs = { count: overdueJobs, variant: 'urgent' }
      } else {
        // If no overdue, check for unscheduled jobs (pending - blue)
        const { count: unscheduledJobs } = await supabase
          .from('jobs')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', currentCompany.id)
          .is('scheduled_date', null)
          .eq('status', 'scheduled')

        if (unscheduledJobs && unscheduledJobs > 0) {
          badges.jobs = { count: unscheduledJobs, variant: 'pending' }
        }
      }

      // Get overdue invoices (urgent - red)
      const { count: overdueInvoices } = await supabase
        .from('invoices')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', currentCompany.id)
        .lt('due_date', today)
        .eq('status', 'sent')

      if (overdueInvoices && overdueInvoices > 0) {
        badges.invoices = { count: overdueInvoices, variant: 'urgent' }
      }

      return badges
    },
    enabled: !!currentCompany,
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000 // Consider data stale after 30 seconds
  })
}
